import {
  CheckCircleOutlined,
  EllipsisOutlined,
  FilterOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  StopOutlined,
} from '@ant-design/icons';
import type { MenuProps, TableProps } from 'antd';
import { Button, Dropdown, Input, Radio, Select, Switch, Table, Tag } from 'antd';
import type { ColumnsType, TableRowSelection } from 'antd/es/table/interface';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PreviewImage } from '@/components/PreviewImage/PreviewImage';
import { ROUTES } from '@/constants/routes';
import { useGlobalLoader } from '@/hooks/useGlobalLoader';
import { useNotify } from '@/hooks/useNotify';
import {
  useBulkDeleteAdminUsersMutation,
  useBulkUpdateAdminUserStatusMutation,
  useDeleteAdminUserMutation,
  useGetAdminUsersQuery,
  useGetPermissionsConfigQuery,
  useUpdateAdminUserMutation,
} from '@/modules/users/usersApi';
import { useAppSelector } from '@/store/hooks';
import type { AdminUser, AdminUserRole, AdminUserStatusFilter } from '@/types/adminUser';
import { ADMIN_USER_ROLE_OPTIONS } from '@/types/adminUser';
import { resolveMutationResult } from '@/utils/apiResponse';
import { confirmBulkDelete, confirmDelete } from '@/utils/confirmDialog';
import { formatDateTime } from '@/utils/date';
import { resolveUserAvatarUrl } from '@/utils/media';
import { getStaffRoleLabel, getUserDisplayName } from '@/utils/user';
import { ColumnFilterPanel } from '../../categories/components/ColumnFilterPanel';
import { SortableColumnTitle } from '../../categories/components/SortableColumnTitle';
import { useUserQueryParams } from '../hooks/useUserQueryParams';
import styles from './AdminUsersTab.module.scss';

function roleTagColor(role: AdminUserRole): string {
  if (role === 'super_admin') return 'gold';
  if (role === 'manager') return 'green';
  return 'default';
}

export function AdminUsersTab() {
  const notify = useNotify();
  const navigate = useNavigate();
  const currentUserId = useAppSelector((store) => store.auth.user?.id);
  const { state, apiParams, patchParams, resetAll, hasActiveFiltersOrSort } = useUserQueryParams();
  const { data: listResult, isLoading, isFetching } = useGetAdminUsersQuery(apiParams);
  const { data: permissionsConfig } = useGetPermissionsConfigQuery();
  useGlobalLoader(isFetching && !!listResult);

  const teamRoles = useMemo(
    () => permissionsConfig?.customProfiles ?? [],
    [permissionsConfig?.customProfiles],
  );

  const roleFilterOptions = useMemo(
    () => [
      { label: 'All roles', value: 'all' as const },
      ...ADMIN_USER_ROLE_OPTIONS.filter((option) => option.value !== 'super_admin'),
    ],
    [],
  );

  const users = listResult?.results ?? [];
  const activeSortBy = listResult?.sortBy ?? state.sortBy;
  const activeSortOrder = listResult?.sortOrder ?? state.sortOrder;
  const total = listResult?.total ?? 0;
  const currentPage = Number(listResult?.page ?? state.page);
  const pageSize = Number(listResult?.limit ?? state.limit);

  const [searchDraft, setSearchDraft] = useState(state.search);
  const [statusDraft, setStatusDraft] = useState<AdminUserStatusFilter>(state.status);
  const [roleDraft, setRoleDraft] = useState<AdminUserRole | 'all'>(state.role);
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);

  const [updateUser] = useUpdateAdminUserMutation();
  const [deleteUser] = useDeleteAdminUserMutation();
  const [bulkUpdateStatus, { isLoading: isBulkUpdating }] = useBulkUpdateAdminUserStatusMutation();
  const [bulkDelete, { isLoading: isBulkDeleting }] = useBulkDeleteAdminUsersMutation();

  const clearSelection = () => setSelectedRowKeys([]);

  const handleSort = useCallback(
    (field: string) => {
      const nextOrder: 'asc' | 'desc' =
        state.sortBy === field ? (state.sortOrder === 'asc' ? 'desc' : 'asc') : 'asc';
      patchParams({ sortBy: field, sortOrder: nextOrder, page: 1 });
    },
    [patchParams, state.sortBy, state.sortOrder],
  );

  const handleStatusChange = useCallback(
    async (user: AdminUser, isActive: boolean) => {
      if (user.id === currentUserId && !isActive) {
        notify.error('You cannot deactivate your own account.');
        return;
      }

      const result = await resolveMutationResult(updateUser({ id: user.id, body: { isActive } }));
      if (result.ok) {
        notify.success(result.message ?? 'Status updated.');
        return;
      }
      notify.error(result.message);
    },
    [currentUserId, notify, updateUser],
  );

  const handleDelete = useCallback(
    async (user: AdminUser) => {
      if (user.id === currentUserId) {
        notify.error('You cannot delete your own account.');
        return;
      }

      const confirmed = await confirmDelete(getUserDisplayName(user));
      if (!confirmed) return;

      const result = await resolveMutationResult(deleteUser(user.id));
      if (result.ok) {
        notify.success(result.message ?? 'User deleted.');
        setSelectedRowKeys((keys) => keys.filter((key) => key !== user.id));
        return;
      }
      notify.error(result.message);
    },
    [currentUserId, deleteUser, notify],
  );

  const handleBulkStatus = async (isActive: boolean) => {
    if (!selectedRowKeys.length) return;

    const result = await resolveMutationResult(
      bulkUpdateStatus({ ids: selectedRowKeys, isActive }),
    );

    if (result.ok) {
      notify.success(result.message ?? 'Users updated.');
      clearSelection();
      return;
    }
    notify.error(result.message);
  };

  const handleBulkDelete = async () => {
    if (!selectedRowKeys.length) return;

    const confirmed = await confirmBulkDelete(selectedRowKeys.length);
    if (!confirmed) return;

    const result = await resolveMutationResult(bulkDelete({ ids: selectedRowKeys }));
    if (result.ok) {
      notify.success(result.message ?? 'Users deleted.');
      clearSelection();
      return;
    }
    notify.error(result.message);
  };

  const getRowActionItems = useCallback(
    (user: AdminUser): MenuProps['items'] => [
      {
        key: 'edit',
        label: 'Edit',
        onClick: () => navigate(`/users/${user.id}/edit`),
      },
      { type: 'divider' },
      {
        key: 'delete',
        label: 'Delete',
        danger: true,
        disabled: user.id === currentUserId,
        onClick: () => void handleDelete(user),
      },
    ],
    [currentUserId, handleDelete, navigate],
  );

  const columns: ColumnsType<AdminUser> = useMemo(
    () => [
      {
        title: (
          <SortableColumnTitle
            label="NAME"
            field="firstName"
            activeField={activeSortBy}
            sortOrder={activeSortOrder}
            onSort={handleSort}
          />
        ),
        key: 'name',
        className: styles.nameCol,
        filteredValue: state.search ? [state.search] : null,
        filterIcon: (filtered) => (
          <SearchOutlined className={filtered ? styles.filterActive : styles.filterIcon} />
        ),
        filterDropdown: ({ close }) => (
          <ColumnFilterPanel
            onApply={() => {
              patchParams({ search: searchDraft.trim() || null }, { resetPage: true });
              close();
            }}
            onReset={() => {
              setSearchDraft('');
              patchParams({ search: null }, { resetPage: true });
              close();
            }}
          >
            <Input
              allowClear
              size="small"
              prefix={<SearchOutlined />}
              placeholder="Search name or email"
              value={searchDraft}
              onChange={(e) => setSearchDraft(e.target.value)}
            />
          </ColumnFilterPanel>
        ),
        render: (_v, record) => (
          <div className={styles.nameRow}>
            <PreviewImage
              src={resolveUserAvatarUrl(record)}
              size={36}
              className={styles.avatar}
              fallback={record.firstName.charAt(0)}
              alt={getUserDisplayName(record)}
            />
            <div className={styles.nameBlock}>
              <span className={styles.nameCell}>{getUserDisplayName(record)}</span>
              <span className={styles.emailCell}>{record.email}</span>
            </div>
          </div>
        ),
      },
      {
        title: 'ROLE',
        dataIndex: 'role',
        key: 'role',
        filterIcon: (filtered) => (
          <FilterOutlined className={filtered ? styles.filterActive : styles.filterIcon} />
        ),
        filteredValue: state.role !== 'all' ? [state.role] : null,
        filterDropdown: ({ close }) => (
          <ColumnFilterPanel
            onApply={() => {
              patchParams({ role: roleDraft === 'all' ? null : roleDraft }, { resetPage: true });
              close();
            }}
            onReset={() => {
              setRoleDraft('all');
              patchParams({ role: null }, { resetPage: true });
              close();
            }}
          >
            <Select
              size="small"
              className={styles.filterSelect}
              value={roleDraft}
              onChange={(value) => setRoleDraft(value)}
              options={roleFilterOptions}
            />
          </ColumnFilterPanel>
        ),
        filterDropdownProps: {
          onOpenChange: (visible) => {
            if (visible) setRoleDraft(state.role);
          },
        },
        render: (_role: AdminUserRole, record: AdminUser) => (
          <Tag color={roleTagColor(record.role)}>{getStaffRoleLabel(record, teamRoles)}</Tag>
        ),
      },
      {
        title: (
          <SortableColumnTitle
            label="STATUS"
            field="isActive"
            activeField={activeSortBy}
            sortOrder={activeSortOrder}
            onSort={handleSort}
          />
        ),
        dataIndex: 'isActive',
        key: 'isActive',
        align: 'center',
        filteredValue: state.status !== 'all' ? [state.status] : null,
        filterIcon: (filtered) => (
          <FilterOutlined className={filtered ? styles.filterActive : styles.filterIcon} />
        ),
        filterDropdown: ({ close }) => (
          <ColumnFilterPanel
            onApply={() => {
              patchParams(
                { status: statusDraft === 'all' ? null : statusDraft },
                { resetPage: true },
              );
              close();
            }}
            onReset={() => {
              setStatusDraft('all');
              patchParams({ status: null }, { resetPage: true });
              close();
            }}
          >
            <Radio.Group value={statusDraft} onChange={(e) => setStatusDraft(e.target.value)}>
              <Radio value="all">All</Radio>
              <Radio value="active">Active</Radio>
              <Radio value="inactive">Inactive</Radio>
            </Radio.Group>
          </ColumnFilterPanel>
        ),
        render: (isActive: boolean, record) => (
          <Switch
            checked={isActive}
            size="small"
            disabled={record.id === currentUserId}
            onChange={(checked) => void handleStatusChange(record, checked)}
          />
        ),
      },
      {
        title: (
          <SortableColumnTitle
            label="LAST LOGIN"
            field="lastLoginAt"
            activeField={activeSortBy}
            sortOrder={activeSortOrder}
            onSort={handleSort}
          />
        ),
        dataIndex: 'lastLoginAt',
        key: 'lastLoginAt',
        render: (value?: string) => (
          <span className={styles.dateCell}>{value ? formatDateTime(value) : '—'}</span>
        ),
      },
      {
        title: (
          <SortableColumnTitle
            label="UPDATED"
            field="updatedAt"
            activeField={activeSortBy}
            sortOrder={activeSortOrder}
            onSort={handleSort}
          />
        ),
        dataIndex: 'updatedAt',
        key: 'updatedAt',
        render: (value: string) => <span className={styles.dateCell}>{formatDateTime(value)}</span>,
      },
      {
        title: 'ACTION',
        key: 'actions',
        align: 'center',
        render: (_v, record) => (
          <Dropdown menu={{ items: getRowActionItems(record) }} trigger={['click']}>
            <Button
              type="text"
              size="small"
              icon={<EllipsisOutlined />}
              className={styles.actionBtn}
            />
          </Dropdown>
        ),
      },
    ],
    [
      activeSortBy,
      activeSortOrder,
      currentUserId,
      getRowActionItems,
      handleSort,
      handleStatusChange,
      patchParams,
      roleDraft,
      roleFilterOptions,
      searchDraft,
      state.role,
      state.search,
      state.status,
      statusDraft,
      teamRoles,
    ],
  );

  const rowSelection: TableRowSelection<AdminUser> = {
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys as string[]),
    getCheckboxProps: (record) => ({
      disabled: record.id === currentUserId,
    }),
  };

  const handleTableChange: TableProps<AdminUser>['onChange'] = (pagination) => {
    patchParams({
      page: pagination.current ?? 1,
      limit: pagination.pageSize ?? pageSize,
    });
  };

  return (
    <div className={styles.wrap}>
      <div className={styles.toolbar}>
        <div>
          <h2 className={styles.title}>Admin users</h2>
          <p className={styles.desc}>Create and manage staff accounts with role-based access.</p>
        </div>
        <div className={styles.toolbarActions}>
          {hasActiveFiltersOrSort ? (
            <Button icon={<ReloadOutlined />} onClick={resetAll}>
              Reset
            </Button>
          ) : null}
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate(ROUTES.USER_CREATE)}
          >
            Add user
          </Button>
        </div>
      </div>

      {selectedRowKeys.length > 0 ? (
        <div className={styles.bulkBar}>
          <span className={styles.bulkCount}>{selectedRowKeys.length} selected</span>
          <Button
            size="small"
            icon={<CheckCircleOutlined />}
            onClick={() => void handleBulkStatus(true)}
            loading={isBulkUpdating}
          >
            Activate
          </Button>
          <Button
            size="small"
            icon={<StopOutlined />}
            onClick={() => void handleBulkStatus(false)}
            loading={isBulkUpdating}
          >
            Deactivate
          </Button>
          <Button size="small" danger onClick={() => void handleBulkDelete()} loading={isBulkDeleting}>
            Delete
          </Button>
          <Button size="small" type="text" onClick={clearSelection}>
            Clear
          </Button>
        </div>
      ) : null}

      <div className={styles.tableCard}>
        <Table<AdminUser>
          className={styles.table}
          rowKey="id"
          size="small"
          scroll={{ x: 'max-content' }}
          loading={isLoading && !listResult}
          columns={columns}
          dataSource={users}
          rowSelection={rowSelection}
          onChange={handleTableChange}
          pagination={{
            current: currentPage,
            pageSize,
            total,
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 50],
            showTotal: (totalCount, [start, end]) => `${start}-${end} of ${totalCount}`,
            size: 'small',
          }}
        />
      </div>
    </div>
  );
}
